<template>
  <div v-if="fields.length" class="flex flex-col">
    <!-- Stands in for a label so the button lines up with the labelled filters beside it:
         a 28px label line plus the 5px margin the legacy global CSS gives every label. -->
    <div class="h-7 mb-[5px]" aria-hidden="true" />

    <LlMenu
      :close-on-content-click="false"
      location="bottom start"
      :min-width="260"
      :max-width="300"
      @menu-open="startEditing"
    >
      <template #activator="{ props: menuProps }">
        <LlSecondaryButton
          v-bind="menuProps"
          variant="outlined"
          density="comfortable"
          class="text-none !bg-[rgb(var(--v-theme-surface))]"
          data-cy="custom-fields-filter-button"
        >
          <LlIcon icon="mdi-filter-variant" size="18" class="mr-2" />
          Custom Fields
          <v-chip v-if="activeCount > 0" size="x-small" color="primary" variant="flat" class="ml-2">
            {{ activeCount }}
          </v-chip>
        </LlSecondaryButton>
      </template>

      <template #content="{ close }">
        <!-- v-card also gives the radio labels a text colour to inherit; the menu overlay has none. -->
        <v-card>
          <div class="flex flex-col gap-3 px-4 pt-3 pb-1 max-h-72 overflow-y-auto">
            <div v-for="field in fields" :key="field.id" :data-cy="`custom-field-filter-${field.id}`">
              <div class="flex items-center justify-between mb-1">
                <span class="text-xs font-medium text-gray-600">{{ field.name }}</span>
                <!-- A radio can't be clicked off, so this is the only way to drop one field's pick. -->
                <LlSecondaryButton
                  v-if="pending[field.id]"
                  variant="text"
                  size="x-small"
                  class="text-none"
                  @click="clearField(field.id)"
                >
                  Clear
                </LlSecondaryButton>
              </div>
              <!-- Exclusive per field: a record holds one value per custom field. -->
              <LlRadioGroup
                :model-value="pending[field.id]"
                :options="optionsFor(field)"
                :inline="false"
                @update:model-value="setPending(field.id, $event)"
              />
            </div>
          </div>
          <div class="flex justify-end gap-2 px-4 py-2">
            <LlSecondaryButton variant="text" size="small" @click="pending = {}">
              Clear all
            </LlSecondaryButton>
            <LlSecondaryButton
              variant="flat"
              size="small"
              color="primary"
              data-cy="custom-fields-filter-apply"
              @click="applyPending(close)"
            >
              Apply
            </LlSecondaryButton>
          </div>
        </v-card>
      </template>
    </LlMenu>

    <div v-if="appliedChips.length" class="flex flex-wrap items-center gap-1 mt-2 max-w-56">
      <!-- White with a thin border, not a tint: the panel behind is a green wash,
           so a tinted chip would be green on green. `.bg-white` is avoided because
           Vuetify's palette class also forces black text. -->
      <v-chip
        v-for="chip in appliedChips"
        :key="chip.fieldId"
        size="small"
        color="primary"
        variant="text"
        closable
        class="!bg-[rgb(var(--v-theme-surface))] border border-outline-variant border-opacity-100"
        @click:close="removeField(chip.fieldId)"
      >
        {{ chip.text }}
      </v-chip>
      <LlSecondaryButton
        variant="text"
        size="x-small"
        class="text-none underline"
        @click="emit('update:modelValue', {})"
      >
        Clear all
      </LlSecondaryButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { PropType } from 'vue';
import LlMenu from '@/components/LlMenu.vue';
import LlIcon from '@/components/LlIcon.vue';
import LlRadioGroup from '@/components/LlRadioGroup.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import { NOT_ASSIGNED_OPTION } from '@/constants/customFieldFilterConstants';
import type {
  CustomFieldFilterSelections,
  FilterableCustomField,
} from '@/models/customFieldFilterTypes';

// Presentational: the page loads its own fields (matter or client) and owns the
// applied selections, so this stays free of any feature's store.
const props = defineProps({
  fields: { type: Array as PropType<FilterableCustomField[]>, required: true },
  modelValue: { type: Object as PropType<CustomFieldFilterSelections>, required: true },
});

const emit = defineEmits<{
  'update:modelValue': [value: CustomFieldFilterSelections];
}>();

// Picks made while the popover is open; only Apply commits them.
const pending = ref<CustomFieldFilterSelections>({});

const startEditing = () => {
  pending.value = { ...props.modelValue };
};

const setPending = (fieldId: string, optionId: string) => {
  pending.value = { ...pending.value, [fieldId]: optionId };
};

const withoutField = (selections: CustomFieldFilterSelections, fieldId: string) => {
  const next = { ...selections };
  delete next[fieldId];
  return next;
};

const clearField = (fieldId: string) => {
  pending.value = withoutField(pending.value, fieldId);
};

const applyPending = (close: () => void) => {
  emit('update:modelValue', { ...pending.value });
  close();
};

const optionsFor = (field: FilterableCustomField) => [
  { label: 'Not assigned', value: NOT_ASSIGNED_OPTION },
  ...(field.enumOptions ?? []).map((opt) => ({ label: opt.value || opt.label || '', value: opt.id })),
];

interface AppliedChip {
  fieldId: string;
  text: string;
}

const appliedChips = computed<AppliedChip[]>(() =>
  Object.entries(props.modelValue)
    .filter(([, value]) => value)
    .map(([fieldId, value]): AppliedChip | null => {
      const field = props.fields.find((f) => f.id === fieldId);
      if (!field) return null;
      const option = (field.enumOptions ?? []).find((o) => o.id === value);
      const label = value === NOT_ASSIGNED_OPTION ? 'Not assigned' : option?.value || option?.label || value;
      return { fieldId, text: `${field.name}: ${label}` };
    })
    .filter((chip): chip is AppliedChip => chip !== null),
);

const activeCount = computed(() => appliedChips.value.length);

const removeField = (fieldId: string) => {
  emit('update:modelValue', withoutField(props.modelValue, fieldId));
};
</script>
